import type { ErrorCode } from "@nano/contracts";
import { en } from "./en.ts";
import { formatNumber, translate, type Translation } from "./lang.ts";

/**
 * UI copy. Vietnamese is the source; `en.ts` holds the English twin and the
 * table follows the player's language (see `lang.ts`).
 * House style: playful and warm, plain words only. No dashes, middle dots,
 * ellipses, slashes, plus or times signs; show an icon from the art set instead.
 */
const source = {
  credit: "Created by nanodev.io.vn",
  loading: "Đang mở cổng thành phố, chờ xíu nha!",
  pick: {
    title: "Hôm nay bạn muốn hóa thân thành ai nè?",
    subtitle: "Đổi ý lúc nào cũng được, cứ vào Cài đặt là xong.",
    random: "Nhắm mắt chọn bừa!",
    restore: "Đã có mã? Khôi phục hành trình ngay",
  },
  hud: {
    shop: "Cửa hàng",
    wardrobe: "Tủ đồ",
    leaderboard: "Xếp hạng",
    settings: "Cài đặt",
    achievements: "Thành tựu",
    pending: "chờ đồng bộ",
    firstHint: "Chạm vào Nhà Sắp Xếp đi, cả núi đồ đang chờ bạn dọn kìa!",
    guest: "Bạn nhỏ",
  },
  lobby: {
    current: (n: number) => `Bạn đang ở màn ${n} trên 100`,
    play: "Chơi tiếp",
    playLevel: (n: number) => `Chơi màn ${n}`,
    playNext: (n: number) => `Chơi tiếp màn ${n}`,
    chapter: (n: number) => `Chương ${n}`,
    chapterName: (n: number, name: string) => `Chương ${n}, ${name}`,
    chapterStars: (n: number) => `${n} trên 30`,
    needNetwork: "Chương này cần mạng để tải lần đầu. Bắt wifi giúp tụi mình nha!",
    allDone: "Bạn phá đảo cả 100 màn rồi! Đỉnh quá trời!",
  },
  construction: {
    title: "Đang xây dựng",
    body: "Thợ xây đang gõ búa cộc cộc. Nhà này sắp mở cửa, ghé lại sau nha!",
    maintenance: "Đang bảo trì",
    maintenanceBody: "Nhà đang được lau chùi cho bóng loáng. Tiến độ của bạn vẫn nằm yên an toàn nha.",
  },
  puzzle: {
    level: (n: number) => `Màn ${n}`,
    moves: (used: number, max: number | null) => (max !== null ? `Còn ${Math.max(0, max - used)} lượt` : `${used} lượt`),
    target: (n: number) => `Mục tiêu ${n}`,
    undo: "Hoàn tác",
    hint: "Gợi ý",
    restart: "Chơi lại",
    pauseTitle: "Nghỉ xíu",
    resume: "Chơi tiếp",
    quit: "Về thành phố",
    outOfMoves: "Ối, hết lượt mất rồi! Làm ván nữa nha?",
    retry: "Thử lại",
    tutorialOk: "Hiểu rồi!",
    hintCost: "Xài gợi ý thì màn này chỉ được 1 sao thôi đó. Vẫn xài hông?",
    hintFree: (n: number) => `Màn này còn ${n} lượt gợi ý miễn phí nè.`,
    hintYes: "Xài luôn!",
    hintLeft: (n: number) => `Còn ${n} lượt gợi ý miễn phí`,
    hintPrice: (n: number) => `Gợi ý giá ${n} vàng`,
    hintBuyTitle: "Hết gợi ý miễn phí rồi",
    hintBuyBody: (price: number) => `Đổi ${price} vàng lấy thêm một gợi ý nha? Vàng được máy chủ trừ, chắc như bắp.`,
    hintBuy: (price: number) => `Đổi ${price} vàng`,
    hintWallet: "Ví của bạn",
    hintPoor: "Chưa đủ vàng rồi, qua thêm vài màn là có liền nè.",
    hintOffline: "Cần có mạng để đổi vàng lấy gợi ý nha.",
    hintBought: "Rinh thêm một gợi ý rồi nè!",
    hintNo: "Thôi, tự nghĩ tiếp",
    hintUndo: "Gợi ý nhỏ nè, hoàn tác bước vừa rồi thử xem!",
    hintNone: "Bóng đèn gợi ý cũng đang bí. Thử nước khác xem sao!",
    loadFailed: "Màn chơi bị kẹt đâu đó trên mạng rồi. Kiểm tra kết nối rồi thử lại nha!",
    finale: "Màn trùm cuối",
    difficulty: ["", "Dễ ợt", "Vừa vừa", "Hơi khoai", "Khoai", "Siêu khoai"] as readonly string[],
  },
  result: {
    title: "Giỏi quá đi!",
    titleByStars: ["", "Qua màn rồi!", "Giỏi quá đi!", "Hoàn hảo không tì vết!"] as readonly string[],
    chapterDone: (n: number) => `Xong chương ${n} rồi nè!`,
    combo: (n: number) => `Chuỗi dài nhất ${n} lần liền`,
    time: (text: string) => `Về đích sau ${text}`,
    newRecord: "Kỷ lục mới toanh!",
    gold: (n: number) => `Nhận ${n} vàng`,
    pendingOffline: "Đã lưu trên máy, có mạng là đồng bộ liền.",
    pending: "Đang chờ máy chủ gật đầu, xíu thôi!",
    confirmed: "Máy chủ gật đầu rồi!",
    improved: (from: number, to: number) => `Từ ${from} sao vọt lên ${to} sao luôn!`,
    replay: "Màn này bạn qua rồi nha, quà lần đầu chỉ nhận một lần thôi.",
    next: "Màn tiếp",
    again: "Chơi lại",
    city: "Về thành phố",
    saveFailed: "Ối, chưa lưu được kết quả trên máy. Thử lại giúp mình nha!",
    needsUpdate: "Màn chơi cần cập nhật xíu",
  },
  shop: {
    title: "Cửa hàng phép thuật",
    wardrobe: "Tủ đồ của bạn",
    owned: "Có rồi",
    wearing: "Đang mặc",
    notEnough: "Thiếu vàng xíu",
    buy: (price: number) => `Mua ${price} vàng`,
    wear: "Mặc",
    remove: "Tháo",
    takeOff: "Đang mặc, chạm để tháo",
    confirm: (name: string, price: number) => `Rinh ${name} về với ${price} vàng nha?`,
    confirmYes: "Rinh về!",
    offline: "Cần mạng để mua đồ nha. Đồ đã có thì cứ thử và mặc thoải mái!",
    disabled: "Cửa hàng đang ngủ trưa, ghé lại sau nha!",
    bought: (name: string) => `Đã mua ${name}! Xinh xỉu luôn!`,
    empty: "Kệ này còn trống trơn, chờ hàng mới về nha!",
    preview: "Thử đồ",
    rarity: { common: "Thường", rare: "Hiếm", epic: "Sử thi" } as Record<string, string>,
    wishlist: "Mong muốn",
    wishlistSet: "Đã ghim làm món mơ ước. Nhìn góc màn hình để theo dõi nha!",
    goalLeft: (n: number) => `Còn ${formatNumber(n)} vàng nữa thôi`,
    goalReady: "Đủ vàng rồi, rinh liền!",
    almost: "Sắp đủ",
    revealTitle: "Món mới về!",
    revealWearing: "Đã mặc lên người rồi nè",
    revealOk: "Xinh quá trời!",
  },
  achievements: {
    title: "Thành tựu",
    unlocked: "Thành tựu mới!",
    unlockedMany: (n: number) => `${n} thành tựu mới một lúc luôn!`,
    claim: (gold: number) => `Nhận ${gold}`,
    claimed: "Đã nhận",
    earned: "Đã đạt",
    badgeOnly: "Huy hiệu",
    progress: (current: number, target: number) => `${current} trên ${target}`,
    summary: (done: number, total: number) => `Bạn đã đạt ${done} trên ${total} thành tựu`,
    rewardLine: (gold: number) => `${gold} vàng đang chờ bạn trong mục Thành tựu`,
    waitSync: "Tiến độ đang trên đường đồng bộ. Đợi vài phút rồi thử lại nha!",
    offline: "Cần mạng để nhận vàng thưởng nha.",
    deviceNote: "Huy hiệu tính theo đồng hồ máy chỉ để sưu tầm cho vui, không kèm vàng đâu nha.",
  },
  leaderboard: {
    title: "Bảng xếp hạng",
    you: (rank: number | null) => (rank ? `Bạn đang đứng hạng ${rank}` : "Bạn chưa có hạng. Kiếm ngôi sao đầu tiên thôi!"),
    beyond: "Bạn đang bám đuổi nhóm dẫn đầu. Cố lên, sắp tới rồi!",
    stale: (time: string) => `Bảng đang chờ cập nhật, bản lưu lúc ${time}.`,
    offline: "Đang mất mạng, tạm xem bảng đã lưu nha.",
    disabled: "Bảng xếp hạng đang đi nghỉ mát.",
    empty: "Bảng còn trống trơn. Bạn sẽ là người cắm cờ đầu tiên!",
    stars: "sao",
    levels: "màn",
    summary: (stars: number, levels: number) => `${stars} sao, ${levels} màn`,
    houseLine: (levels: number, stars: number) => `${levels} màn, ${stars} sao`,
    houses: "Tiến độ các nhà",
    up: (n: number) => `Vọt lên ${n} hạng!`,
    down: (n: number) => `Tụt ${n} hạng, gỡ lại thôi!`,
    update: "Cập nhật thứ hạng của tôi",
    updating: "Đang cập nhật…",
    updated: "Xong! Bảng đã có tên và tiến độ mới nhất của bạn.",
    updatePending: "Vẫn còn chút dữ liệu chờ gửi, đợi vài giây rồi bấm lại nha.",
    updateFailed: "Chưa cập nhật được, thử lại sau chút nha.",
  },
  settings: {
    title: "Cài đặt",
    sound: "Âm thanh",
    music: "Nhạc nền",
    musicSoon: "Sắp có",
    language: "Ngôn ngữ",
    app: "Ứng dụng Android",
    getApk: "Tải ứng dụng Android",
    getApkNote: "Cài một lần thôi, từ đó mỗi khi có bản mới là game tự cập nhật luôn.",
    vibration: "Rung",
    name: "Tên hiển thị",
    save: "Lưu",
    nameSaved: "Đã lưu tên! Nghe kêu ghê!",
    nameRejected: "Tên này chưa dùng được, thử tên khác nha.",
    character: "Đổi nhân vật",
    backup: "Lưu hành trình",
    backupBody:
      "Tạo mã khôi phục bí mật để mang hành trình sang máy khác. Giữ thật kín nha, ai có mã là dùng được hành trình của bạn đó!",
    backupNeedSync: "Kết nối mạng để đồng bộ tiến độ trước khi tạo mã nha.",
    newCode: "Tạo mã mới, mã cũ sẽ hết phép",
    codeTitle: "Mã khôi phục của bạn",
    codeOnce: "Mã chỉ hiện đúng một lần thôi. Chép lại và cất thật kỹ nha!",
    copy: "Sao chép",
    copied: "Chép xong!",
    qr: "Hiện mã QR",
    restore: "Khôi phục hành trình",
    restoreBody: "Nhập mã khôi phục vào đây. Hành trình trên máy này sẽ được thay bằng hành trình trong mã.",
    restorePlaceholder: "Dán mã khôi phục vào đây",
    restoreConfirm: "Thay hành trình hiện tại trên máy này thật hả?",
    restoreYes: "Thay luôn!",
    restored: "Hành trình đã về nhà!",
    honest: "Nếu chưa từng đồng bộ hoặc lỡ mất cả mã lẫn dữ liệu máy thì hành trình không cứu được đâu nha.",
    storageBlocked: "Trình duyệt đang chặn lưu trên máy, đóng trang là có thể mất tiến độ đó.",
    sync: "Đồng bộ",
    syncState: {
      idle: "Đã đồng bộ",
      syncing: "Đang đồng bộ, chờ xíu",
      offline: "Mất mạng rồi, nhưng vẫn chơi bình thường nha",
      error: "Lát nữa mình thử lại",
      disabled: "Đồng bộ đám mây đang nghỉ xả hơi",
      session_lost: "Phiên đã hết hạn. Dùng mã khôi phục để kết nối lại nha",
    },
    pendingCount: (n: number) => `${n} màn đang chờ đồng bộ`,
    rejectedCount: (n: number) => `${n} màn chơi cần cập nhật xíu`,
    about: "Giới thiệu",
    version: (v: string) => `Phiên bản ${v}`,
    tag: (tag: string) => `Mã người chơi ${tag}`,
    privacy: "Không cần tài khoản. Tụi mình không xin email hay số điện thoại đâu.",
  },
  update: {
    ready: "Có bản cập nhật mới nè!",
    apply: "Cập nhật",
    later: "Để sau",
    apk: "Có phiên bản ứng dụng mới",
    apkBody: "Tải bản mới để nhận thêm nhiều điều thú vị. Tiến độ của bạn vẫn giữ nguyên nha.",
    download: "Tải bản mới",
    title: "Cập nhật",
    running: (version: string) => `Đang chạy bản ${version}`,
    check: "Kiểm tra cập nhật",
    checking: "Đang hỏi máy chủ xem có gì mới, chờ xíu nha",
    latest: "Bạn đang dùng bản mới cứng rồi!",
    downloading: "Đang tải bản cập nhật về máy",
    readyBody: "Bản mới nằm sẵn trong máy rồi. Bấm cập nhật là xong, tiến độ vẫn giữ nguyên nha.",
    applyNow: "Cập nhật ngay",
    apkReady: (version: string) => `Có ứng dụng mới bản ${version}`,
    failed: "Chưa hỏi được máy chủ. Có mạng rồi thử lại nha.",
    auto: "Có mạng là game tự tải bản mới, mở lại lần sau là xong.",
  },
  getApp: {
    title: "Ứng dụng Android",
    body: "Chơi mượt hơn, tràn màn hình và tự cập nhật khi có bản mới.",
    step1: "Tạo mã khôi phục để mang hành trình sang ứng dụng.",
    step2: "Tải tệp APK rồi mở ra để cài. Máy có hỏi thì cứ cho phép cài từ nguồn này nha.",
    step3: "Mở ứng dụng, chọn Khôi phục hành trình rồi nhập mã.",
    download: (size: string) => (size ? `Tải APK nặng ${size}` : "Tải APK"),
    unavailable: "Bản APK đang được nướng trong lò, ghé lại sau nha!",
    makeCode: "Tạo mã khôi phục",
    scan: "Đang dùng máy tính? Mở camera điện thoại Android quét mã này là tải được liền.",
    android: "Tệp APK chỉ cài được trên điện thoại Android thôi nha.",
  },
  install: {
    title: "Thêm vào Màn hình chính",
    body: "Muốn chơi tràn màn hình, tải nhanh hơn và giữ tiến độ chắc hơn trên iPhone hay iPad thì làm thế này nha!",
    step1: "Chạm nút Chia sẻ ở thanh công cụ Safari",
    step2: "Chọn Thêm vào MH chính, tiếng Anh là Add to Home Screen",
    step3: "Mở trò chơi từ biểu tượng mới trên màn hình chính",
    continue: "Chơi tiếp trên Safari",
    note: "Safari có thể dọn mất dữ liệu web lâu không dùng. Bản trên màn hình chính an toàn hơn nhiều.",
  },
  preload: {
    title: "Đang chuẩn bị thành phố",
    body: "Tải trước toàn bộ hình ảnh để chơi mượt cả khi mạng yếu.",
    progress: (done: number, total: number) => `Đã tải ${done} trên ${total} tệp`,
    failed: "Vài tệp đi lạc chưa về.",
    retry: "Thử lại",
    skip: "Vào chơi luôn",
  },
  wait: "Chờ xíu nha",
  starsLabel: (n: number) => `${n} sao`,
  rotate: "Xoay điện thoại dọc lại để chơi nha!",
  contextLost: "Đồ họa vừa ngủ gật. Chạm để tải lại, tiến độ vẫn được lưu nha.",
  reload: "Tải lại",
  close: "Đóng",
  cancel: "Để sau",
  ok: "Đồng ý",
  duration: {
    hours: (h: number, m: number) => (m > 0 ? `${h} giờ ${m} phút` : `${h} giờ`),
    minutes: (m: number, s: number) => (s > 0 ? `${m} phút ${s} giây` : `${m} phút`),
    seconds: (s: number) => `${s} giây`,
  },
  errors: {
    notEnoughGold: "Chưa đủ vàng. Chơi thêm vài màn là có ngay!",
    owned: "Món này bạn có rồi nha.",
    badCode: "Mã chưa đúng. Soi lại từng ký tự giúp mình nha.",
    slowDown: "Từ từ thôi nào, nghỉ xíu rồi thử lại nha.",
    resting: "Tính năng này đang đi nghỉ. Bạn vẫn chơi bình thường nha.",
    network: "Không kết nối được. Cứ chơi tiếp, lát nữa mình thử lại.",
    content: "Màn chơi cần cập nhật xíu.",
    achievement: "Máy chủ chưa thấy thành tựu này. Chờ chút nha.",
    other: "Ối, có chút trục trặc. Thử lại sau nha!",
  },
} as const;

export type UiStrings = Translation<typeof source>;

export const vi: UiStrings = translate(source, en);

/** 42 s -> "42 giây", 185 s -> "3 phút 5 giây", 3725 s -> "1 giờ 2 phút". */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return vi.duration.hours(hours, minutes);
  if (minutes > 0) return vi.duration.minutes(minutes, seconds);
  return vi.duration.seconds(seconds);
}

/** Friendly copy for API errors; children never see raw technical errors. */
export function friendlyError(code: ErrorCode | "NETWORK" | "TIMEOUT"): string {
  switch (code) {
    case "NOT_ENOUGH_GOLD":
      return vi.errors.notEnoughGold;
    case "ITEM_ALREADY_OWNED":
      return vi.errors.owned;
    case "INVALID_RECOVERY_CODE":
      return vi.errors.badCode;
    case "RECOVERY_RATE_LIMITED":
    case "RATE_LIMITED":
    case "PROFILE_COOLDOWN":
      return vi.errors.slowDown;
    case "FEATURE_DISABLED":
    case "SERVICE_TEMPORARILY_UNAVAILABLE":
      return vi.errors.resting;
    case "NETWORK":
    case "TIMEOUT":
      return vi.errors.network;
    case "UNSUPPORTED_CONTENT_VERSION":
      return vi.errors.content;
    case "ACHIEVEMENT_LOCKED":
      return vi.errors.achievement;
    default:
      return vi.errors.other;
  }
}
